// Plan 30-02 — LetterCubeSprite: 9-layer Pixi Container + 6-state FSM +
// reduced-motion fork + Hollow Option A.
//
// Phase 31 (RUNE-30) extension: imperative flashHollowRejection(reducedMotion)
// method for WordTraceLayer's 4-channel Hollow rejection (red overlay flash +
// last-segment path flash + vibrate(50) + screen-reader announce). The flash
// overlay is parallel to setState's _activeStateTween (Pitfall 5) and adds
// _activeHollowFlashTween + _activeHollowFlashTimeout bookkeeping drained in
// destroy() — both forks are idempotent re: in-flight cancel/restart.
//
// One sprite per board cell (60 cells max). LetterLayer (Plan 30-03) owns
// the SHARED tweedle Group + ParticleEmitter and passes them via props so
// (a) tween updates fire once per frame from the Pixi Ticker (Pitfall 5
// single-RAF discipline established by tweedle-bootstrap.ts) and (b) the
// destroy contract can drain only THIS sprite's tweens from the group on
// unmount — leaving sibling sprites' tweens intact.
//
// 9-layer z-order (UI-SPEC §2):
//   z=0 tileBase            Sprite (cube_letter_X frame)
//   z=1 letterGlyph         Text (Fredoka 700)
//   z=2 tierEmbellishment   Sprite (forged/rare/mythic — null for common)
//   z=3 tintWash            Graphics rect (α=0.25 std / α=1.0 Anchor)
//   z=4 crossHatch          Sprite (Player B std cells only — colorblind)
//   z=5 affinityHalo        Sprite — SLOT only in P30 (visible=false always)
//   z=6 anchorSigil         Sprite (top-right corner, owner-tinted)
//   z=7 hollowReplacement   Sprite (Option A — tint-baked single-texture)
//   z=8 particleMount       Container (rune-carve / mythic-sparkle host)
//
// 6-state FSM (UI-SPEC §4):
//   idle → spawning-in:  200ms cubic-bezier-bouncy y-rise + alpha + particle
//   idle → hover:        100ms ease-snap-out scale 1.0 → 1.05
//   idle → in-trace:     100ms ease-snap-out tier alpha 1.0 → 0.7
//   idle-anchor → hollow: 400ms ease-cinematic cross-fade + Option-A flip
//   Mythic idle pulse:   yoyo sine 1400ms on tierEmbellishment.alpha 0.7↔1.0
//   Anchor idle pulse:   yoyo sine 2400ms on anchorSigil.alpha 0.85↔1.0
//
// Reduced-motion fork (UI-SPEC §5):
//   spawn-in → 200ms linear alpha only (no y-rise, no particles)
//   hover    → cross-fade (no scale geometry)
//   pulses   → suspended entirely (peak alpha static)
//   anchor→hollow → 200ms simultaneous cross-fade
//
// Hollow Option A (Open Q1 lock + RESEARCH §5 Pitfall 7):
//   _setHollowVisibility(true) hides z=0..6;
//   hollowReplacement.tint = blendTint(retainedTint, hollowCrackColor, 0.6)
//   preserves the cell.tint signature so colorblind users see who-was-here.
//
// Pitfall 5 destroy lock: destroy() iterates _idleTweens + _activeStateTween
// and removes each from this.tweenGroup BEFORE super.destroy(). Without
// this, a destroyed sprite's tweens would keep the sprite ref alive past
// container teardown and Group.shared.update() would still try to mutate
// .alpha / .y / .scale on a destroyed Pixi.Container.

import {
  Container,
  Graphics,
  type Sprite as PixiSprite,
  Sprite,
  Text,
  type Texture,
} from 'pixi.js'
import { Easing, type Group, Tween } from 'tweedle.js'
import { $boardReanchor } from '../../stores/board-reanchor'
import { $cascadeFrontier } from '../../stores/cascade-frontier'
import { actionTokens, hexToInt } from '../../styles/tokens'
import type { ParticleEmitter } from '../motion/particles'
import { createPositionAnchorTween } from '../motion/position-anchor-tween'
import {
  cubicBezierEasing,
  EASE_BOUNCY_CUBIC_BEZIER,
  selectDuration,
} from '../motion/tween-config'
import type { RuneAtlasTextures } from '../theme/atlas-loader'
import type { RuneThemeTokens } from '../theme/extract-rune-tokens'

/**
 * 6-state cell FSM per UI-SPEC §4.
 *
 *   idle          — standard cell at rest
 *   idle-anchor   — Anchor at rest (slow tint pulse, sigil visible)
 *   hollow        — consumed Anchor (terminal — no exit transitions)
 *   spawning-in   — just refilled / just revealed (bouncy rise + alpha)
 *   in-trace      — inside an active word-trace path
 *   hover         — pointer-over (drag-trace start hint)
 */
export type CellState =
  | 'idle'
  | 'idle-anchor'
  | 'hollow'
  | 'spawning-in'
  | 'in-trace'
  | 'hover'

/**
 * Runespring cell payload — mirrors `PlayerView.board[]` shape from
 * apps/web/src/game/PlayerViewSchema.ts.
 *
 * Server-authoritative per DEC-021 B12 firewall — the sprite renders these
 * fields, but never mutates them. updateCell() diffs against a private
 * previous-cell snapshot to decide which sub-sprites to swap.
 */
export interface CellData {
  cell_idx: number
  letter: string | null
  tier: 'common' | 'forged' | 'rare' | 'mythic'
  tint: 'a' | 'b' | null
  is_anchor: boolean
  is_hollow: boolean
  anchor_owner: 0 | 1 | null
}

// Cell geometry constants (UI-SPEC §2 + amendment §3.1).
export const CELL_SIZE_PX = 64
export const ANCHOR_SIGIL_SIZE_PX = Math.round(CELL_SIZE_PX * 0.18) // ~12px

/** Letter-glyph fill — folk warm ink on the cream tile (Inkwell token). The old
 * dark-purple 0x231650 inkwell now resolves to the folk inkwell (#3b2a1c).
 * Standard cells draw their ownership tint at α=0.25 over the cream tile, so the
 * warm inkwell stays well above WCAG AA there. Anchor cells are the exception —
 * see ANCHOR_GLYPH_FILL. */
export const LETTER_GLYPH_FILL = hexToInt(actionTokens.inkwell)

/** Anchor-cell letter-glyph fill — a warm near-black (#0d0905).
 *
 * Anchor cells draw a FULL-opacity (α=1.0) ownership tint wash directly behind
 * the glyph (see `_redrawTintWash`). Against the saturated player-B red tint
 * (`letterTintPlayerB` = #e64545) the standard warm inkwell (#3b2a1c) lands at
 * only ~3.46:1 — below the 4.5:1 AA threshold (ARCA-120 / GH#111). A LIGHT fill
 * cannot rescue it: that red is a mid-luminance color, so even pure white tops
 * out at ~3.96:1 on it (and white is a disastrous ~1.8:1 on the teal player-A
 * wash). The only fill that clears AA on BOTH opaque tints is a near-black one —
 * #0d0905 measures ~5.0:1 on red-B and ~10.9:1 on teal-A. Locked by the contrast
 * assertions in LetterCubeSprite.test.ts so it can't silently regress. */
export const ANCHOR_GLYPH_FILL = 0x0d0905

/** Thin warm-cream outline (textHalo) on the Anchor glyph. The near-black fill
 * already meets AA against today's two tints; the stroke adds a crisp edge that
 * reads against ANY tint, so the Anchor letter stays legible if more ownership
 * colors are ever introduced (PM guidance, ARCA-120). Standard cells get no
 * stroke. */
const ANCHOR_GLYPH_STROKE = hexToInt(actionTokens.textHalo)
const ANCHOR_GLYPH_STROKE_WIDTH = 3

/** Glyph fill + outline for a cell's letter, keyed on whether it's an Anchor.
 * Pure (no Pixi objects) so it is trivially unit-testable: the contrast suite
 * asserts `anchorGlyphStyle(true).fill` clears WCAG AA against both player tints.
 * Anchors get the near-black fill + cream outline; standard cells get the warm
 * inkwell and no stroke. */
export function anchorGlyphStyle(isAnchor: boolean): {
  fill: number
  stroke: { color: number; width: number } | null
} {
  return isAnchor
    ? {
        fill: ANCHOR_GLYPH_FILL,
        stroke: {
          color: ANCHOR_GLYPH_STROKE,
          width: ANCHOR_GLYPH_STROKE_WIDTH,
        },
      }
    : { fill: LETTER_GLYPH_FILL, stroke: null }
}

/** Folk reskin (brief §Design — rarity carries 1/2/3/5 point pips). The code
 * tiers common/forged/rare/mythic map to the brief's plain/fine/rare/royal. */
const PIP_COUNT_BY_TIER: Readonly<Record<CellData['tier'], number>> = {
  common: 1,
  forged: 2,
  rare: 3,
  mythic: 5,
}

/** Common tier has no rune gem token — use the folk point/florin gold. */
const PIP_COMMON_COLOR = hexToInt(actionTokens.currencyCoin)

/** Hollow Option A blend factor — 0.6 weights toward hollowCrackColor while
 * preserving 0.4 of the retained cell tint (UI-SPEC §3.1 + RESEARCH §5
 * Pitfall 7 lock). Empirically tuned in 30-RESEARCH; not exposed as a token
 * because it's a perceptual constant, not a theme decision. */
const HOLLOW_TINT_BLEND_T = 0.6

export interface LetterCubeSpriteProps {
  cell: CellData
  theme: RuneThemeTokens
  /** Snapshot of $reducedMotion.get() at construction. Live OS flips trigger
   * LetterLayer-level re-construction (Plan 30-03), NOT in-sprite mutation. */
  reducedMotion: boolean
  textures: RuneAtlasTextures
  /** Shared LetterLayer-level Group — T-30-02 mitigation (one Group → one
   * ticker.add → 60 cells × N tweens batched on a single frame callback). */
  tweenGroup: Group
  /** Shared particle emitter — caller throttles to reduce per-cell rate. */
  particles: ParticleEmitter
}

/**
 * LetterCubeSprite extends Pixi.Container. One per board cell.
 *
 * Construction is synchronous and side-effect-free apart from adding
 * children to `this`. setState() + updateCell() are the only public
 * mutation surfaces. destroy() drains all owned tweens before
 * super.destroy() (Pitfall 5 lock — verified by Task-3 leak test).
 */
export class LetterCubeSprite extends Container {
  // 9 sub-sprite refs in z-order. Optional ones (forged/rare/mythic ring;
  // crosshatch; anchor sigil) are `null` when the cell shape doesn't need
  // them — null-checks in setState / updateCell handle the absence.
  private readonly _tileBase: PixiSprite
  private readonly _letterGlyph: Text
  private _tierEmbellishment: PixiSprite | null
  private readonly _tintWash: Graphics
  private _crossHatch: PixiSprite | null
  private readonly _affinityHalo: Graphics // #2 — own-letter affinity ring
  private _anchorSigil: PixiSprite | null
  private readonly _hollowReplacement: PixiSprite
  private readonly _particleMount: Container
  /** Folk reskin — additive z=9 rarity point-pip sub-layer. Appended ABOVE the
   * 9 fixed z-slots (z0..z8) so it never renumbers the preserved z-order
   * contract. Drawn with `.stroke()` (round-cap dots), like _affinityHalo. */
  private readonly _pipLayer: Graphics

  // FSM + tween bookkeeping.
  private _state: CellState
  private readonly _reducedMotion: boolean
  private readonly _tweenGroup: Group
  private readonly _particles: ParticleEmitter
  private readonly _textures: RuneAtlasTextures
  /** Cached cell snapshot for updateCell() diff. */
  private _prevCell: CellData
  /** Cached theme snapshot for updateCell() re-tint. */
  private _prevTheme: RuneThemeTokens
  /** Mythic + Anchor pulse tweens (yoyo loops). Empty array under reduced
   * motion — pulses are suspended entirely, not paused, per UI-SPEC §5. */
  private readonly _idleTweens: Tween<object>[] = []
  /** The single in-flight transition tween (setState replaces this; destroy
   * drains it). Null when in a non-tweening rest state. */
  private _activeStateTween: Tween<object> | null = null
  /** #2 — the affinity ring's idle "breathe" pulse (yoyo alpha loop). Null when
   *  the ring is hidden or under reduced motion. Drained in destroy() per the
   *  Pitfall 5 lock, like the other in-flight tweens. */
  private _affinityPulse: Tween<object> | null = null
  /** #2 — the affinity ring's peak (rest) alpha, captured from the theme token
   *  when the ring is (re)drawn; the pulse dips below it and returns to it. */
  private _affinityPeakAlpha = 0
  /** Phase 31 (RUNE-30) — in-flight Hollow-rejection red flash overlay tween
   * (standard-motion path). Parallel to `_activeStateTween` per Pitfall 5
   * (Group.shared lock + destroy contract — both fields are drained in
   * destroy() before super.destroy() so the shared Group's next update()
   * cannot mutate a destroyed Container's children). */
  private _activeHollowFlashTween: Tween<object> | null = null
  /** Phase 31 (RUNE-30) — in-flight Hollow-rejection cleanup timeout handle
   * (reduced-motion path). Reduced-motion has NO tween — the overlay sits
   * static at α=0.6 for 200ms then is cleared via window.setTimeout. The
   * handle is stored so destroy() can `clearTimeout` it and so a re-trigger
   * before completion can cancel + restart cleanly. */
  private _activeHollowFlashTimeout: number | null = null
  /** Phase 32 Plan 32-05 (RUNE-35) — cascade-frontier subscription teardown.
   * Subscribes in the constructor; called in destroy() to drop the
   * subscription so $cascadeFrontier updates after teardown cannot fire
   * setState on a destroyed Container (Pitfall 3 mitigation companion). */
  private _cascadeFrontierUnsubscribe: (() => void) | null = null
  /** Phase 32 Plan 32-05 — one-shot guard for the cascade-frontier-driven
   * spawning-in trigger. Once a sprite self-fires setState('spawning-in')
   * in response to a frontier increment, subsequent increments MUST NOT
   * re-fire the transition (RESEARCH §A4 Pitfall 3 — eliminates the race
   * between FlopReveal's manual setState call and LetterLayer's mount of
   * the new cell sprite). */
  private _hasSpawnedIn = false
  /** Phase 32 Plan 32-05 — caller's current_turn_idx threaded down from
   * LetterLayer. Phase 2 (court-opens-at-the-center) dropped the turn-window
   * gating from `_maybeStartSpawningIn` (the new center-cohort signal flows from
   * `centerGrowWindow` via `_isNewThisGrow`/`_cohortOrdinal` instead), so this
   * value is no longer read for spawn-in gating. The `setCurrentTurnIdx` setter
   * + this field are RETAINED as a stable public mount contract (LetterLayer
   * still threads `current_turn_idx` down) and a hook for any future turn-aware
   * sprite behavior — hence the intentional write-only retention below. */
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: retained as the stable setCurrentTurnIdx mount contract (see comment) — written by the mount each render, read by future turn-aware behavior
  private _currentTurnIdx = 0
  /** Phase 2 (court-opens-at-the-center) — true when THIS cell is part of the
   * NEW center-inserted cohort for the current grown board (driven by
   * board-layout's `centerGrowWindow`, threaded down from LetterLayer). Replaces
   * the stale `cell_idx>=36/48` index-window math in `_maybeStartSpawningIn` —
   * after the MID growth, those high indices are SHIFTED existing cells, not the
   * new cohort. LetterLayer sets this via `setIsNewThisGrow` on every render. */
  private _isNewThisGrow = false
  /** Phase 2 — this cell's 0-based ordinal WITHIN the new center cohort
   * (cell_idx - window.start, range 0..11). The cascade gate fires when
   * `_cohortOrdinal < frontier`, so the seam bursts open cell-by-cell as the
   * reveal FSM ramps the frontier 0..12. 0 for non-cohort cells. */
  private _cohortOrdinal = 0

  // Phase 4 Task 5 — re-anchor glide fields.
  /** In-flight position re-anchor tween (old→new logical pos). Null at rest. */
  private _activeReanchorTween: Tween<{ x: number; y: number }> | null = null
  /** Queued logical position to glide to on the next $boardReanchor pulse.
   * Null when no reanchor is pending. Full motion: held until pulse fires.
   * Reduced motion: applied immediately in queueReanchor (field stays null). */
  private _pendingReanchor: { x: number; y: number } | null = null
  /** Teardown handle for the $boardReanchor nanostores subscription. */
  private _boardReanchorUnsubscribe: (() => void) | null = null
  /** Last $boardReanchor epoch handled — guards against the nanostores
   * emit-on-subscribe firing for a pre-existing nonzero epoch on fresh mount. */
  private _lastReanchorEpoch = 0

  constructor(props: LetterCubeSpriteProps) {
    super()
    this._reducedMotion = props.reducedMotion
    this._tweenGroup = props.tweenGroup
    this._particles = props.particles
    this._textures = props.textures
    this._prevCell = { ...props.cell }
    this._prevTheme = { ...props.theme }

    // ---- z=0 tileBase --------------------------------------------------
    this._tileBase = new Sprite(
      props.textures.tileBase(props.cell.letter ?? ' '),
    )
    this._tileBase.width = CELL_SIZE_PX
    this._tileBase.height = CELL_SIZE_PX
    this.addChild(this._tileBase)

    // ---- z=1 tierEmbellishment (optional; tier='common' has no overlay) -
    this._tierEmbellishment = this._buildTierEmbellishment(
      props.cell.tier,
      props.theme,
      props.textures,
    )
    if (this._tierEmbellishment) {
      this.addChild(this._tierEmbellishment)
    }

    // ---- z=2 tintWash ---------------------------------------------------
    this._tintWash = new Graphics()
    this._redrawTintWash(props.cell, props.theme)
    this.addChild(this._tintWash)

    // ---- z=3 crossHatch (Player B standard cells only — colorblind) -----
    this._crossHatch = this._buildCrossHatch(props.cell, props.textures)
    if (this._crossHatch) {
      this.addChild(this._crossHatch)
    }

    // ---- z=4 affinityHalo — your-letter affinity RING (#2) --------------
    // Marks board letters that match the caller's affinity runes. Phase 30
    // shipped this as a faint full-cell cream FILL (α=0.18) that was invisible
    // on a phone; #2 reworks it into a high-contrast gold EDGE RING that hugs
    // the tile border and breathes (idle pulse) so owned letters are
    // unmistakable while keeping the glyph legible (an outline, not a wash).
    // Visibility is bound to the caller's affinity set by updateAffinity();
    // colour/alpha come from the theme tokens and are (re)drawn here + on
    // theme change.
    this._affinityHalo = new Graphics()
    this._affinityHalo.visible = false
    this._redrawAffinityRing(props.theme)
    this.addChild(this._affinityHalo)

    // ---- z=5 letterGlyph (ABOVE the tint/hatch/halo washes) -------------
    // The letter renders on top of the ownership tint so it stays legible even
    // on Anchor cells, whose tint wash is fully opaque (α=1.0) — otherwise the
    // opaque wash would completely hide the letter (VisualDesign §6.3: the
    // Anchor's letter face must remain readable; the sigil sits in the corner).
    const initialGlyphStyle = anchorGlyphStyle(props.cell.is_anchor)
    this._letterGlyph = new Text({
      text: (props.cell.letter ?? '').toUpperCase(),
      style: {
        fontFamily: 'Fredoka',
        fontWeight: '700',
        fontSize: 28,
        fill: initialGlyphStyle.fill,
        align: 'center',
        // Anchor cells get a thin cream outline (near-black fill + cream stroke
        // = legible on the opaque ownership tint); standard cells have none.
        ...(initialGlyphStyle.stroke
          ? { stroke: initialGlyphStyle.stroke }
          : {}),
      },
    })
    // Center within the cell — anchor at 0.5 centers the text, position at
    // CELL_SIZE_PX/2 places the anchor at the cell center.
    this._letterGlyph.anchor.set(0.5)
    this._letterGlyph.x = CELL_SIZE_PX / 2
    this._letterGlyph.y = CELL_SIZE_PX / 2
    this.addChild(this._letterGlyph)

    // ---- z=6 anchorSigil (optional; only when is_anchor) ----------------
    this._anchorSigil = this._buildAnchorSigil(
      props.cell,
      props.theme,
      props.textures,
    )
    if (this._anchorSigil) {
      this.addChild(this._anchorSigil)
    }

    // ---- z=7 hollowReplacement -----------------------------------------
    // ALWAYS built; visibility toggled by _setHollowVisibility.
    this._hollowReplacement = new Sprite(props.textures.hollowCrackedRuneStone)
    this._hollowReplacement.width = CELL_SIZE_PX
    this._hollowReplacement.height = CELL_SIZE_PX
    this._hollowReplacement.visible = false
    this.addChild(this._hollowReplacement)

    // ---- z=8 particleMount ---------------------------------------------
    this._particleMount = new Container()
    this.addChild(this._particleMount)

    // ---- z=9 pipLayer (folk reskin, ADDITIVE — does NOT renumber z0..8) --
    // Rarity point pips (1/2/3/5 dots by tier) in the bottom-right corner,
    // tinted by the tier gem color. Static (reduced-motion-safe).
    this._pipLayer = new Graphics()
    this._redrawPips(props.cell, props.theme)
    this.addChild(this._pipLayer)

    // ---- Initial state + Hollow tint bake + pulse start -----------------
    if (props.cell.is_hollow) {
      this._setHollowVisibility(true)
      this._hollowReplacement.tint = this._computeHollowTint(
        props.cell,
        props.theme,
      )
      this._state = 'hollow'
    } else if (props.cell.is_anchor) {
      this._state = 'idle-anchor'
    } else {
      this._state = 'idle'
    }

    // Start idle pulses iff not reduced motion AND tier/anchor warrants.
    this._startIdlePulses(props.cell, props.theme)

    // Phase 2 (court-opens-at-the-center) — subscribe to $cascadeFrontier so the
    // sprite self-fires setState('spawning-in') when it is part of the NEW
    // center-inserted cohort and its cohort ordinal is within the active
    // frontier. The subscription is unconditional; the `_maybeStartSpawningIn`
    // predicate gates by `_isNewThisGrow` + `_cohortOrdinal < frontier` +
    // `_hasSpawnedIn` one-shot guard, so non-cohort cells and post-spawn
    // increments are no-ops. Pitfall 3 mitigation per RESEARCH §A4: new sprites
    // subscribe on mount and immediately receive the current frontier value via
    // nanostores' emit-current-value semantics — no race with LetterLayer mount
    // order.
    this._cascadeFrontierUnsubscribe = $cascadeFrontier.subscribe(
      (frontier) => {
        this._maybeStartSpawningIn(frontier)
      },
    )

    // Phase 4 Task 5 — subscribe to the floor-drop release epoch so the
    // sprite can start its old→new position glide at the right beat.
    // We capture the current epoch BEFORE subscribing so the immediate
    // emit-on-subscribe (nanostores fires synchronously with the current
    // value) is skipped when the atom is already nonzero (e.g. this sprite
    // was mounted after the current reveal already pulsed).
    this._lastReanchorEpoch = $boardReanchor.get()
    this._boardReanchorUnsubscribe = $boardReanchor.subscribe((epoch) => {
      // Correct under the monotonic-epoch contract: $boardReanchor is never
      // reset in production, so every real pulse strictly increments the epoch
      // and always passes this guard. Only the synchronous emit-on-subscribe
      // (nanostores fires the current value immediately on .subscribe()) is
      // skipped when the atom is already nonzero at construction time.
      if (epoch <= this._lastReanchorEpoch) return
      this._lastReanchorEpoch = epoch
      this._maybeRunReanchor()
    })
  }

  /** Phase 32 Plan 32-05 (RUNE-35) — caller's current_turn_idx setter.
   * LetterLayer calls this on every render so the sprite always knows the
   * current turn when the frontier atom emits. Idempotent. */
  setCurrentTurnIdx(idx: number): void {
    this._currentTurnIdx = idx
  }

  /** Phase 2 (court-opens-at-the-center) — flag this cell as part of (or not
   * part of) the new center-inserted cohort for the current grown board.
   * LetterLayer derives the value from `centerGrowWindow(board.length)` and
   * calls this on every render. Idempotent. */
  setIsNewThisGrow(isNew: boolean): void {
    this._isNewThisGrow = isNew
  }

  /** Phase 2 — set this cell's ordinal within the new center cohort
   * (cell_idx - window.start). Used by `_maybeStartSpawningIn` to gate the
   * seam-burst cascade against the reveal FSM's frontier. Idempotent. */
  setCohortOrdinal(ordinal: number): void {
    this._cohortOrdinal = ordinal
  }

  /** Phase 4 Task 5 — record the new logical position WITHOUT moving the
   * sprite (full motion: glide on the next $boardReanchor pulse).
   * Reduced motion: snap now (no hold, no tween). */
  queueReanchor(x: number, y: number): void {
    if (this._reducedMotion) {
      this.x = x
      this.y = y
      return
    }
    this._pendingReanchor = { x, y }
  }

  /** Phase 4 Task 5 — start the old→new position glide when the
   * $boardReanchor pulse fires and a pending reanchor has been queued.
   * No-op when nothing is queued. Cancels any in-flight glide first. */
  private _maybeRunReanchor(): void {
    const target = this._pendingReanchor
    if (target === null) return
    this._pendingReanchor = null
    if (this._activeReanchorTween) {
      this._tweenGroup.remove(this._activeReanchorTween)
      this._activeReanchorTween = null
    }
    const duration = selectDuration('dur-board-grow', this._reducedMotion)
    // Identity-guard: capture the tween ref before assignment so stale
    // onComplete callbacks cannot clobber a newer tween (matches the
    // _clearActiveStateTween / activeRef discipline used by flashHollowRejection).
    //
    // Phase 2 "doors fling open": pass the bouncy curve so existing cells PART
    // with a mild overshoot as the seam opens (the new cohort bursts UP from
    // the center while the survivors glide outward and settle past their mark).
    // The overshoot is the easing's control-y > 1 excursion — at the
    // 12px-per-reveal MID step this lands ~5px past the mark then settles, so it
    // never travels off-screen. Reduced motion → duration 0 → snap (the easing
    // is ignored on the duration<=0 path in createPositionAnchorTween).
    const tweenRef = createPositionAnchorTween({
      target: this,
      fromPos: { x: this.x, y: this.y },
      toPos: target,
      duration,
      group: this._tweenGroup,
      easing: EASE_BOUNCY_CUBIC_BEZIER,
      onComplete: () => {
        if (this._activeReanchorTween === tweenRef) {
          this._activeReanchorTween = null
        }
      },
    })
    this._activeReanchorTween = tweenRef
  }

  /** Phase 2 (court-opens-at-the-center) — cascade-frontier gate.
   *
   * Fires `setState('spawning-in')` when ALL conditions are met:
   *   - `_hasSpawnedIn` is false (one-shot per sprite lifetime).
   *   - The state is not already 'spawning-in' (defensive — setState
   *     short-circuits on same-state, but the explicit check keeps this
   *     readable).
   *   - This cell is part of the NEW center-inserted cohort (`_isNewThisGrow`),
   *     AND its cohort ordinal is within the active frontier
   *     (`_cohortOrdinal < frontier`) — so the seam bursts open cell-by-cell as
   *     the reveal FSM ramps the frontier 0..12.
   *
   * The OLD `cell_idx>=36/48` + turn math is GONE: after the backend's
   * deepen-the-middle growth, those high indices are SHIFTED existing cells, not
   * the new cohort. The cohort signal now flows from board-layout's
   * `centerGrowWindow` via LetterLayer → setIsNewThisGrow/setCohortOrdinal.
   *
   * Reduced-motion accessibility (LOAD-BEARING): under reduced motion the reveal
   * FSM never ramps the frontier past 0, so `_cohortOrdinal < frontier` is
   * always false → no sprite ever self-fires spawning-in here. New cells appear
   * at their final on-board position with no scale/bounce/particles — an instant
   * snap. Do NOT drop the `&& _cohortOrdinal < frontier` clause.
   *
   * Pitfall 3 mitigation: by owning the spawn-in trigger inside the sprite,
   * we eliminate the cell-cascade sprite-not-found race that would occur
   * if FlopReveal/RiverReveal tried to call `letterCubeSprites[idx].setState(
   * 'spawning-in')` before LetterLayer had a chance to mount the new
   * sprite for that idx. */
  private _maybeStartSpawningIn(frontier: number): void {
    if (this._hasSpawnedIn) return
    if (this._state === 'spawning-in') return
    if (this._isNewThisGrow && this._cohortOrdinal < frontier) {
      this._hasSpawnedIn = true
      this.setState('spawning-in')
    }
  }

  /** Readonly state access — Task-3 tests + WordTraceLayer (P31) read this. */
  get state(): CellState {
    return this._state
  }

  /**
   * Trigger an FSM transition. If `next === current`, no-op. Otherwise
   * cancel any in-flight transition tween, look up the UI-SPEC §4 tween
   * config for the new transition, and start it. Pulse tweens are NOT
   * affected here — they run independently in _idleTweens.
   */
  setState(next: CellState): void {
    if (next === this._state) return

    // Cancel any in-flight state transition before starting a new one.
    if (this._activeStateTween) {
      this._tweenGroup.remove(this._activeStateTween)
      this._activeStateTween = null
    }

    const prev = this._state
    this._state = next

    if (next === 'spawning-in') {
      this._runSpawningInTween()
    } else if (next === 'hover') {
      this._runHoverInTween()
    } else if (next === 'idle' || next === 'idle-anchor') {
      // Return-from-* transitions: hover-out, trace-release, spawn-complete.
      if (prev === 'hover') {
        this._runHoverOutTween()
      } else if (prev === 'in-trace') {
        this._runInTraceReleaseTween()
      }
      // From 'spawning-in' → idle/idle-anchor is the natural completion of
      // _runSpawningInTween's onComplete callback — no separate tween.
    } else if (next === 'in-trace') {
      this._runInTraceEnterTween()
    } else if (next === 'hollow') {
      this._runHollowTween()
    }
  }

  /**
   * Phase 31 (RUNE-32) — bind the affinity halo's visibility to the caller's
   * 4-letter affinity set. Called from LetterCubeSpriteMount on every render
   * (the mount memoizes the set so the call is cheap when affinityLetters
   * reference is stable).
   *
   * Predicate (UI-SPEC §13 T-31-01 — caller-only consumption preserved at the
   * mount layer; this sprite-level method never knows whose affinity set it
   * is, it just consumes the ReadonlySet it was given):
   *   _affinityHalo.visible = (
   *     letter !== null &&
   *     !is_hollow &&
   *     affinitySet.has(letter)
   *   )
   *
   * - No tween: the halo is a static tint+alpha sprite from Phase 30; flipping
   *   visibility is instant. The "appearance" affordance is the static halo
   *   itself, not an entrance animation. (Pulse-on-claim is a DOM concern in
   *   AffinityChipRow — separate surface, separate animation.)
   * - Idempotent: only writes `_affinityHalo.visible` when the desired value
   *   differs from the current one.
   * - Hollow safety: a cell that turned Hollow mid-match never re-shows its
   *   halo even if its prior letter remained in the caller's affinity set
   *   (the affinity letter is now untraceable through the Hollow cell).
   * - Null-letter safety: refilled-in-progress cells (letter=null briefly,
   *   is_hollow=false) cleanly resolve to visible=false without throwing.
   */
  updateAffinity(affinitySet: ReadonlySet<string>): void {
    const want =
      this._prevCell.letter !== null &&
      !this._prevCell.is_hollow &&
      affinitySet.has(this._prevCell.letter)
    if (this._affinityHalo.visible === want) return
    this._affinityHalo.visible = want
    // #2 — bind the idle "breathe" pulse to the ring's visibility so owned
    // letters draw the eye. Reduced motion keeps the static ring (the pulse is
    // suspended, not paused — UI-SPEC §5), so the affordance still reads.
    if (want) this._startAffinityPulse()
    else this._stopAffinityPulse()
  }

  /** #2 — (re)draw the affinity ring outline from the theme tokens. The stroke
   *  is baked at full alpha; the container `.alpha` carries the peak opacity so
   *  the idle pulse can interpolate `.alpha` cleanly (the same trick the
   *  Hollow-flash overlay uses). Called at construction and on theme change. */
  private _redrawAffinityRing(theme: RuneThemeTokens): void {
    const width = 4 // ring stroke (logical px; CELL_SIZE_PX = 64)
    const inset = width / 2
    this._affinityPeakAlpha = theme.affinityHaloOpacity
    this._affinityHalo
      .clear()
      .roundRect(
        inset,
        inset,
        CELL_SIZE_PX - width,
        CELL_SIZE_PX - width,
        12, // corner radius — matches the rounded tile face
      )
      .stroke({
        width,
        color: theme.affinityHaloColor,
        alpha: 1,
        alignment: 0.5,
      })
    this._affinityHalo.alpha = this._affinityPeakAlpha
  }

  /** #2 — start the ring's breathing pulse (yoyo alpha peak↔dip). No-op under
   *  reduced motion, when already running, or when the duration token resolves
   *  to 0. Registered with the shared tweedle Group + drained in destroy(). */
  private _startAffinityPulse(): void {
    if (this._reducedMotion) return
    if (this._affinityPulse !== null) return
    const dur = selectDuration('dur-pulse-anchor', false)
    if (dur <= 0) return
    this._affinityHalo.alpha = this._affinityPeakAlpha // start at peak; yoyo dips
    const tween = new Tween(this._affinityHalo, this._tweenGroup)
      .to({ alpha: this._affinityPeakAlpha * 0.5 }, dur / 2)
      .easing(Easing.Sinusoidal.InOut)
      .yoyo(true)
      .repeat(Number.POSITIVE_INFINITY)
      .start()
    this._affinityPulse = tween as unknown as Tween<object>
  }

  /** #2 — stop the ring pulse (ring hidden) and settle back to the static peak
   *  alpha so a later re-show (or reduced motion) rests at full opacity. */
  private _stopAffinityPulse(): void {
    if (this._affinityPulse === null) return
    this._tweenGroup.remove(this._affinityPulse)
    this._affinityPulse = null
    this._affinityHalo.alpha = this._affinityPeakAlpha
  }

  /**
   * Phase 31 (RUNE-30) — fire the Hollow-rejection red overlay flash on this
   * cell when the user's word-trace tries to pass through it. The overlay is
   * a temporary ember `--color-cta-destructive` (#c73c28) full-cell
   * Graphics child layered above all other sub-sprites.
   *
   * Standard motion (reducedMotion=false): 200ms ease-out alpha 1.0 → 0
   * (UI-SPEC §4 + §5). The tween is registered with the shared tweedle Group
   * via the same Pitfall 5 lock as setState's `_activeStateTween` — drained
   * in destroy() before super.destroy().
   *
   * Reduced motion (reducedMotion=true): NO tween — the overlay sits static
   * at α=0.6 for 200ms then is cleared via window.setTimeout (UI-SPEC §5 row
   * 1 — vibration is suspended elsewhere by the caller).
   *
   * Idempotent: calling while a previous flash is in-flight cancels the
   * prior overlay (removes from container + destroys it + clears the
   * bookkeeping field) before starting the new one. Used by
   * WordTraceLayer's compound feedback (Plan 31-02 Task 3) — the player can
   * drag-and-hold against a Hollow rune and the layer fires per-pointermove
   * rejections; this guarantees at most ONE overlay per sprite at any time.
   */
  flashHollowRejection(reducedMotion: boolean): void {
    // Cancel any in-flight flash first — find + remove the prior overlay
    // child(ren) tagged by the bookkeeping fields below.
    if (this._activeHollowFlashTween !== null) {
      this._tweenGroup.remove(this._activeHollowFlashTween)
      this._activeHollowFlashTween = null
    }
    if (this._activeHollowFlashTimeout !== null) {
      window.clearTimeout(this._activeHollowFlashTimeout)
      this._activeHollowFlashTimeout = null
    }
    // Drain any prior Hollow-flash overlay children left over from a cancelled
    // run. The overlay is the only Graphics we add post-construction with no
    // semantic role beyond the flash, so we can identify it via a label
    // tag. (Pre-existing `_tintWash` is also a Graphics but is added during
    // construction and never removed here.)
    for (const child of [...this.children]) {
      if (
        child instanceof Graphics &&
        (child as Graphics & { _isHollowFlashOverlay?: boolean })
          ._isHollowFlashOverlay === true
      ) {
        this.removeChild(child)
        child.destroy()
      }
    }

    const overlay = new Graphics() as Graphics & {
      _isHollowFlashOverlay: boolean
    }
    overlay._isHollowFlashOverlay = true
    // Bake the fill at full alpha; we drive transparency via the container's
    // `.alpha` so the standard-motion tween can interpolate `alpha` cleanly
    // (Pixi v8 `Graphics.fill({alpha})` bakes into the geometry's
    // FillStyle, not into the container alpha — animating that is non-
    // trivial). Container alpha multiplies with fill alpha at composite time.
    overlay
      .rect(0, 0, CELL_SIZE_PX, CELL_SIZE_PX)
      // Folk reskin: rejection flash uses --color-cta-destructive (folk ember
      // #c73c28) per UI-SPEC §3 token table (was old dark-theme crimson).
      .fill({ color: hexToInt(actionTokens.ctaDestructive), alpha: 1.0 })
    overlay.alpha = reducedMotion ? 0.6 : 1.0
    this.addChild(overlay)

    if (reducedMotion) {
      // Static α=0.6 for 200ms — UI-SPEC §5 row 1 fallback. No tween.
      this._activeHollowFlashTimeout = window.setTimeout(() => {
        // Idempotent re-entry guard: only tear down if the overlay is still
        // a child (a follow-up call may have removed it).
        if (overlay.parent === this) {
          this.removeChild(overlay)
          overlay.destroy()
        }
        this._activeHollowFlashTimeout = null
      }, 200)
      return
    }

    // Standard motion — 200ms ease-out alpha 1.0 → 0.
    const tween = new Tween(overlay, this._tweenGroup)
      .to({ alpha: 0 }, 200)
      .easing(Easing.Cubic.Out)
      .onComplete(() => {
        if (overlay.parent === this) {
          this.removeChild(overlay)
          overlay.destroy()
        }
        // Only null the field if we still own it (a follow-up flash may have
        // overwritten the reference — let the new tween win).
        if (this._activeHollowFlashTween === activeRef) {
          this._activeHollowFlashTween = null
        }
      })
      .start()
    const activeRef = tween as unknown as Tween<object>
    this._activeHollowFlashTween = activeRef
  }

  /**
   * Apply a server-authoritative cell + theme update. Diffs against the
   * cached previous snapshot and re-builds / re-tints the affected layers.
   * Transitions to a new state via setState() when the cell shape changes
   * (e.g., is_hollow flipped true → drives 'hollow' setState).
   */
  updateCell(cell: CellData, theme: RuneThemeTokens): void {
    const prev = this._prevCell

    // is_hollow flip: Anchor → Hollow drives setState('hollow') (full tween).
    // The reverse (Hollow → non-Hollow) is server-illegal (Hollows are
    // terminal per UI-SPEC §4); we log and refuse to mutate.
    if (cell.is_hollow && !prev.is_hollow) {
      this.setState('hollow')
      // After tween completes, _setHollowVisibility(true) fires; tint baked.
      this._hollowReplacement.tint = this._computeHollowTint(cell, theme)
    } else if (!cell.is_hollow && prev.is_hollow) {
      // Server contract violation — Hollows can't be un-hollowed.
      // eslint-disable-next-line no-console
      console.warn(
        `[LetterCubeSprite] cell ${cell.cell_idx}: ignored attempt to ` +
          `un-hollow (Hollows are terminal per UI-SPEC §4 + DEC-021).`,
      )
      return
    }

    // Tier change → swap embellishment.
    if (cell.tier !== prev.tier) {
      if (this._tierEmbellishment) {
        this.removeChild(this._tierEmbellishment)
        this._tierEmbellishment.destroy()
        this._tierEmbellishment = null
      }
      this._tierEmbellishment = this._buildTierEmbellishment(
        cell.tier,
        theme,
        this._textures,
      )
      if (this._tierEmbellishment) {
        // Re-insert at z=1 (right above tileBase, below the washes + letter).
        this.addChildAt(this._tierEmbellishment, 1)
      }
      // Folk reskin: pip count/color follow the tier.
      this._redrawPips(cell, theme)
      // Restart Mythic pulse if applicable.
      this._restartIdlePulses(cell, theme)
    }

    // Anchor flip → add/remove sigil.
    if (cell.is_anchor !== prev.is_anchor) {
      // Re-key the glyph fill/outline: Anchors render over a full-opacity tint
      // wash, so they need the near-black fill + cream outline for WCAG AA
      // legibility; standard cells revert to the warm inkwell, no stroke.
      this._applyGlyphStyle(cell.is_anchor)
      if (this._anchorSigil) {
        this.removeChild(this._anchorSigil)
        this._anchorSigil.destroy()
        this._anchorSigil = null
      }
      this._anchorSigil = this._buildAnchorSigil(cell, theme, this._textures)
      if (this._anchorSigil) {
        // Insert at z=6 (current children: tileBase 0, letterGlyph 1,
        // tier? 2, tintWash, crossHatch?, affinityHalo, hollow, particles).
        // Just addChild; layer ordering is established by addChild order at
        // construction, and tier/crosshatch may also be missing — re-insert
        // at the right slot regardless of current children layout.
        this.addChildAt(this._anchorSigil, this._getAnchorSigilInsertIndex())
      }
      this._restartIdlePulses(cell, theme)
      // Phase 32 Plan 32-05 (RUNE-35) — stone-carve emit on new-Anchor
      // spawn (Anchor placement + Quill Claim mode). UI-SPEC §2 row 10 +
      // PATTERNS §"LetterCubeSprite extend". Emits 10 particles (brass
      // sparks + rune-glyph ghost); the particles.ts reduced flag suspends
      // the burst under reduced motion (returns []).
      if (cell.is_anchor && !prev.is_anchor) {
        this._particles.emit({
          kind: 'stone-carve',
          count: 10,
          reduced: this._reducedMotion,
          x: this.x + CELL_SIZE_PX / 2,
          y: this.y + CELL_SIZE_PX / 2,
        })
      }
    }

    // Tint or Anchor-promotion change → redraw tintWash.
    if (cell.tint !== prev.tint || cell.is_anchor !== prev.is_anchor) {
      this._redrawTintWash(cell, theme)
    }

    // CrossHatch eligibility change.
    const wantsHatch = cell.tint === 'b' && !cell.is_anchor && !cell.is_hollow
    const hasHatch = this._crossHatch !== null
    if (wantsHatch !== hasHatch) {
      if (this._crossHatch) {
        this.removeChild(this._crossHatch)
        this._crossHatch.destroy()
        this._crossHatch = null
      }
      this._crossHatch = wantsHatch
        ? this._buildCrossHatch(cell, this._textures)
        : null
      if (this._crossHatch) {
        // Insert at z=4 (after tintWash at z=3).
        this.addChildAt(this._crossHatch, this._getCrossHatchInsertIndex())
      }
    }

    // Theme change re-tint (cheap: idempotent setProperty equivalents).
    if (theme !== this._prevTheme) {
      if (this._tierEmbellishment) {
        this._tierEmbellishment.tint = this._pickTierColor(cell.tier, theme)
      }
      if (this._anchorSigil && cell.anchor_owner !== null) {
        this._anchorSigil.tint =
          cell.anchor_owner === 0
            ? theme.anchorSigilColorPlayerA
            : theme.anchorSigilColorPlayerB
      }
      this._redrawAffinityRing(theme)
      if (cell.is_hollow) {
        this._hollowReplacement.tint = this._computeHollowTint(cell, theme)
      }
      // Tint wash always re-drawn on theme change.
      this._redrawTintWash(cell, theme)
      // Folk reskin: re-tint pips to the new theme's tier gem colors.
      this._redrawPips(cell, theme)
    }

    // Letter change → swap glyph + tileBase texture.
    if (cell.letter !== prev.letter) {
      this._letterGlyph.text = (cell.letter ?? '').toUpperCase()
      this._tileBase.texture = this._textures.tileBase(cell.letter ?? ' ')
    }

    this._prevCell = { ...cell }
    this._prevTheme = { ...theme }
  }

  /**
   * Destroy this sprite. Pitfall 5 lock: drains _idleTweens +
   * _activeStateTween from the shared Group BEFORE super.destroy() so the
   * Group's next update() pass cannot mutate a destroyed Container's
   * fields.
   */
  override destroy(options?: { children?: boolean }): void {
    // 1. Drain idle pulse tweens.
    for (const t of this._idleTweens) {
      this._tweenGroup.remove(t)
    }
    this._idleTweens.length = 0
    // 1b. #2 — drain the affinity-ring breathe pulse (Pitfall 5 destroy lock).
    if (this._affinityPulse !== null) {
      this._tweenGroup.remove(this._affinityPulse)
      this._affinityPulse = null
    }
    // 2. Drain in-flight transition tween.
    if (this._activeStateTween) {
      this._tweenGroup.remove(this._activeStateTween)
      this._activeStateTween = null
    }
    // 3. Phase 31 — drain in-flight Hollow-flash bookkeeping (standard +
    //    reduced-motion forks). Mirrors Pitfall 5 destroy contract.
    if (this._activeHollowFlashTween !== null) {
      this._tweenGroup.remove(this._activeHollowFlashTween)
      this._activeHollowFlashTween = null
    }
    if (this._activeHollowFlashTimeout !== null) {
      window.clearTimeout(this._activeHollowFlashTimeout)
      this._activeHollowFlashTimeout = null
    }
    // 4. Phase 32 Plan 32-05 — drop the $cascadeFrontier subscription so a
    //    post-destroy atom update cannot invoke setState on a destroyed
    //    Container. Pitfall 5 contract extension for atom subscribers.
    if (this._cascadeFrontierUnsubscribe !== null) {
      this._cascadeFrontierUnsubscribe()
      this._cascadeFrontierUnsubscribe = null
    }
    // 5. Phase 4 Task 5 — drain in-flight re-anchor tween + drop the
    //    $boardReanchor subscription (Pitfall 5 destroy lock extension).
    if (this._activeReanchorTween) {
      this._tweenGroup.remove(this._activeReanchorTween)
      this._activeReanchorTween = null
    }
    if (this._boardReanchorUnsubscribe !== null) {
      this._boardReanchorUnsubscribe()
      this._boardReanchorUnsubscribe = null
    }
    // 6. Pixi container teardown (default to destroying children).
    super.destroy({ children: options?.children ?? true })
  }

  // ---- private builders ---------------------------------------------------

  private _buildTierEmbellishment(
    tier: CellData['tier'],
    theme: RuneThemeTokens,
    textures: RuneAtlasTextures,
  ): PixiSprite | null {
    if (tier === 'common') return null
    let tex: Texture
    switch (tier) {
      case 'forged':
        tex = textures.tierForgedRing
        break
      case 'rare':
        tex = textures.tierRareInlay
        break
      case 'mythic':
        tex = textures.tierMythicGlow
        break
    }
    const s = new Sprite(tex)
    s.width = CELL_SIZE_PX
    s.height = CELL_SIZE_PX
    s.tint = this._pickTierColor(tier, theme)
    s.alpha = 1.0
    return s
  }

  private _pickTierColor(
    tier: CellData['tier'],
    theme: RuneThemeTokens,
  ): number {
    switch (tier) {
      case 'forged':
        return theme.tierForgedRingColor
      case 'rare':
        return theme.tierRareInlayColor
      case 'mythic':
        return theme.tierMythicGlowColor
      default:
        return 0xffffff // common never reaches here; defensive default
    }
  }

  private _buildCrossHatch(
    cell: CellData,
    textures: RuneAtlasTextures,
  ): PixiSprite | null {
    // Player B standard cells only — UI-SPEC §2.11 colorblind dual encoder.
    if (cell.tint !== 'b' || cell.is_anchor || cell.is_hollow) return null
    const s = new Sprite(textures.tintCrosshatchPattern)
    s.width = CELL_SIZE_PX
    s.height = CELL_SIZE_PX
    s.alpha = 0.25 // matches tint wash alpha for visual cohesion
    return s
  }

  private _buildAnchorSigil(
    cell: CellData,
    theme: RuneThemeTokens,
    textures: RuneAtlasTextures,
  ): PixiSprite | null {
    if (!cell.is_anchor) return null
    const s = new Sprite(textures.anchorSigilRune)
    s.width = ANCHOR_SIGIL_SIZE_PX
    s.height = ANCHOR_SIGIL_SIZE_PX
    // Top-right corner, 2px inset (UI-SPEC §2 + amendment §3.1).
    s.x = CELL_SIZE_PX - ANCHOR_SIGIL_SIZE_PX - 2
    s.y = 2
    s.tint =
      cell.anchor_owner === 0
        ? theme.anchorSigilColorPlayerA
        : theme.anchorSigilColorPlayerB
    s.alpha = 1.0
    return s
  }

  /**
   * Re-key the letter glyph's fill + outline on an Anchor-status flip. Anchors
   * render the glyph over a full-opacity ownership tint wash, so they swap to
   * the near-black fill + thin cream outline (WCAG AA on both player tints);
   * standard cells use the warm inkwell with no stroke. Pixi v8: assigning
   * `style.fill`/`style.stroke` triggers a glyph re-render; `stroke = null`
   * clears the outline.
   */
  private _applyGlyphStyle(isAnchor: boolean): void {
    const glyph = anchorGlyphStyle(isAnchor)
    this._letterGlyph.style.fill = glyph.fill
    // Pixi v8 removes the outline when stroke is `null` (verified), but its
    // StrokeInput setter type omits null — assign through a loosened ref rather
    // than fabricate a width:0 sentinel (which would read back as a stroke
    // object, not "no stroke").
    ;(this._letterGlyph.style as { stroke: unknown }).stroke = glyph.stroke
  }

  private _redrawTintWash(cell: CellData, theme: RuneThemeTokens): void {
    this._tintWash.clear()
    if (cell.tint === null) return // no wash on Hollows (covered by flip)
    const color =
      cell.tint === 'a' ? theme.letterTintPlayerA : theme.letterTintPlayerB
    // Standard ownership-tint wash α=0.25 on every cell (Anchors stay at 1.0).
    // The onboarding-only 0.5 boost was removed — the tint is now constant.
    const alpha = cell.is_anchor ? 1.0 : 0.25
    this._tintWash.rect(0, 0, CELL_SIZE_PX, CELL_SIZE_PX).fill({ color, alpha })
  }

  private _computeHollowTint(cell: CellData, theme: RuneThemeTokens): number {
    // Option A: blend retained cell tint × hollow-crack color (RESEARCH §5
    // Pitfall 7 lock). Preserves the cell.tint signature so colorblind
    // users + tournament reviewers can still see which player consumed
    // the Anchor — vs Option B which would lose that signal.
    let retainedTint: number
    if (cell.tint === 'a') {
      retainedTint = theme.letterTintPlayerA
    } else if (cell.tint === 'b') {
      retainedTint = theme.letterTintPlayerB
    } else {
      // No tint signal — fall back to a WARM folk grey (textTertiary taupe)
      // blended with the crack, rather than a cold neutral grey.
      retainedTint = hexToInt(actionTokens.textTertiary)
    }
    return this._blendTint(
      retainedTint,
      theme.hollowCrackColor,
      HOLLOW_TINT_BLEND_T,
    )
  }

  /**
   * Linear-interpolate two 0xRRGGBB color ints, component-wise.
   * t=0 → a; t=1 → b. Used for Hollow Option A tint baking.
   */
  private _blendTint(a: number, b: number, t: number): number {
    const ar = (a >> 16) & 0xff
    const ag = (a >> 8) & 0xff
    const ab = a & 0xff
    const br = (b >> 16) & 0xff
    const bg = (b >> 8) & 0xff
    const bb = b & 0xff
    const r = Math.round(ar + (br - ar) * t)
    const g = Math.round(ag + (bg - ag) * t)
    const bl = Math.round(ab + (bb - ab) * t)
    return (r << 16) | (g << 8) | bl
  }

  /**
   * Hollow Option A visibility flip — hide z=0..6, show z=7. Used at
   * construction (when cell.is_hollow=true) and at the end of the
   * idle-anchor → hollow tween.
   */
  private _setHollowVisibility(hollow: boolean): void {
    this._tileBase.visible = !hollow
    this._letterGlyph.visible = !hollow
    if (this._tierEmbellishment) this._tierEmbellishment.visible = !hollow
    this._tintWash.visible = !hollow
    if (this._crossHatch) this._crossHatch.visible = !hollow
    // affinityHalo stays visible=false always in P30 — slot only.
    if (this._anchorSigil) this._anchorSigil.visible = !hollow
    // Folk reskin: pips are meaningless on a consumed (Hollow) cell.
    this._pipLayer.visible = !hollow
    this._hollowReplacement.visible = hollow
  }

  /** Folk reskin — (re)draw the rarity point pips (1/2/3/5 by tier) in the
   *  bottom-right corner, tinted by the tier gem color. Empty on Hollow cells.
   *  Uses round-cap strokes (degenerate point subpaths) so, like the affinity
   *  ring, it never calls Graphics.fill — keeping the tintWash fill accounting
   *  intact. Static: no tween (reduced-motion-safe). */
  private _redrawPips(cell: CellData, theme: RuneThemeTokens): void {
    this._pipLayer.clear()
    if (cell.is_hollow) return
    const count = PIP_COUNT_BY_TIER[cell.tier]
    const color = this._pickPipColor(cell.tier, theme)
    const diameter = 5 // round-cap stroke width = pip diameter
    const gap = 7 // center-to-center spacing
    const cornerInset = 7 // from the bottom + right cell edges
    const cy = CELL_SIZE_PX - cornerInset
    const rightX = CELL_SIZE_PX - cornerInset
    for (let i = 0; i < count; i++) {
      const cx = rightX - (count - 1 - i) * gap
      this._pipLayer.moveTo(cx, cy).lineTo(cx, cy)
    }
    this._pipLayer.stroke({
      width: diameter,
      color,
      alpha: 0.92,
      cap: 'round',
    })
  }

  /** Folk reskin — pip tint by tier. Common has no rune gem token → folk gold;
   *  the others reuse the rune tier gem colors already on the theme. */
  private _pickPipColor(
    tier: CellData['tier'],
    theme: RuneThemeTokens,
  ): number {
    switch (tier) {
      case 'forged':
        return theme.tierForgedRingColor
      case 'rare':
        return theme.tierRareInlayColor
      case 'mythic':
        return theme.tierMythicGlowColor
      default:
        return PIP_COMMON_COLOR
    }
  }

  private _startIdlePulses(cell: CellData, theme: RuneThemeTokens): void {
    if (this._reducedMotion) return
    if (cell.is_hollow) return // Hollows have no pulses.

    // Mythic pulse on tierEmbellishment.alpha (0.7 ↔ 1.0).
    if (cell.tier === 'mythic' && this._tierEmbellishment) {
      const dur = selectDuration('dur-pulse-mythic', false)
      if (dur > 0) {
        // Halve dur because yoyo doubles the round-trip period.
        const tween = new Tween(this._tierEmbellishment, this._tweenGroup)
          .to({ alpha: 0.7 }, dur / 2)
          .easing(Easing.Sinusoidal.InOut)
          .yoyo(true)
          .repeat(Number.POSITIVE_INFINITY)
          .start()
        this._idleTweens.push(tween as unknown as Tween<object>)
      }
      // Suppress lint — `theme` arg reserved for future per-theme pulse
      // intensity scaling; not used in P30.
      void theme
    }

    // Anchor pulse on anchorSigil.alpha (0.85 ↔ 1.0).
    if (cell.is_anchor && this._anchorSigil) {
      const dur = selectDuration('dur-pulse-anchor', false)
      if (dur > 0) {
        this._anchorSigil.alpha = 1.0 // start at peak (yoyo dips to 0.85)
        const tween = new Tween(this._anchorSigil, this._tweenGroup)
          .to({ alpha: 0.85 }, dur / 2)
          .easing(Easing.Sinusoidal.InOut)
          .yoyo(true)
          .repeat(Number.POSITIVE_INFINITY)
          .start()
        this._idleTweens.push(tween as unknown as Tween<object>)
      }
    }
  }

  private _restartIdlePulses(cell: CellData, theme: RuneThemeTokens): void {
    for (const t of this._idleTweens) {
      this._tweenGroup.remove(t)
    }
    this._idleTweens.length = 0
    this._startIdlePulses(cell, theme)
  }

  /**
   * Null-clear `_activeStateTween` on natural tween completion. Each
   * `_runXxxTween` registers this via its `onComplete` so the field reflects
   * the live tween state (WR-03): without this, the reference lingers after
   * the tween finishes and any future "transition in progress?" probe based
   * on `_activeStateTween !== null` would yield a false positive. We compare
   * identity before clearing so a follow-up `setState()` that started a
   * different tween wins (its assignment is the new ground truth).
   */
  private _clearActiveStateTween(expected: Tween<object>): void {
    if (this._activeStateTween === expected) {
      this._activeStateTween = null
    }
  }

  // ---- tween wirings per UI-SPEC §4 ---------------------------------------

  private _runSpawningInTween(): void {
    const cell = this._prevCell
    // Preserve the externally-supplied base position; the spawn-in tween
    // animates relative to it (Plan 30-03 fix — Rule 1 bug from Plan
    // 30-02: hardcoded `this.y = 0` clobbered the caller-set Y, breaking
    // the swatch grid layout under reduced-motion refill triggers).
    const baseY = this.y
    if (this._reducedMotion) {
      // 200ms linear alpha 0→1, NO y-rise, NO particles (UI-SPEC §5).
      this.alpha = 0
      const t = new Tween(this, this._tweenGroup)
        .to({ alpha: 1 }, 200)
        .easing(Easing.Linear.None)
        .onComplete(() => {
          this._clearActiveStateTween(t as unknown as Tween<object>)
          this.setState(cell.is_anchor ? 'idle-anchor' : 'idle')
        })
        .start()
      this._activeStateTween = t as unknown as Tween<object>
    } else {
      // ── MAXIMAL "court opens at the center seam" burst (Phase 2) ──────────
      // A new center-inserted cell flings UP out of the seam with a big bouncy
      // overshoot. ONE proxy tween drives every channel (Pixi can only tween
      // properties of one target per Tween, so we proxy a 0→1 progress `p` and
      // apply scale / alpha / y from it):
      //   • SCALE about the cell CENTER: 0.45 → 1.08 (overshoot) → 1.0. The
      //     bouncy cubic-bezier (ease-bouncy token, control-y > 1) gives the
      //     overshoot for free, so we lerp scale linearly in `p` from 0.45 → 1.0
      //     and the easing's >1 excursion pushes it past 1.0 mid-flight.
      //   • ALPHA 0 → 1.
      //   • Y rise baseY+8 → baseY (the existing seam lift).
      //   • midpoint rune-carve particle (KEPT).
      //   • a brief gold shimmer accent flashed on settle (≥90% progress).
      //
      // Scale-about-center requires pivoting at the cell center and shifting
      // x/y by +CELL_SIZE_PX/2 so the cell does not drift toward the top-left as
      // it scales. On completion we RESTORE pivot to (0,0) and x/y by
      // -CELL_SIZE_PX/2 so the final on-board top-left is byte-identical to the
      // pre-tween position (existing tests assert sprite.y === baseY / final
      // pos at completion).
      const easing = cubicBezierEasing(...EASE_BOUNCY_CUBIC_BEZIER)
      const half = CELL_SIZE_PX / 2
      const baseX = this.x
      // Pivot about the cell center + compensate so the visible top-left stays
      // put while the sprite scales.
      this.pivot.set(half)
      this.x = baseX + half
      this.y = baseY + half
      const SCALE_FROM = 0.45
      this.scale.set(SCALE_FROM)
      this.alpha = 0
      // The y-rise is applied on the pivoted coordinate (centre y = baseY+half
      // at rest). Start 8px lower (centre y = baseY + 8 + half).
      const yStart = baseY + 8 + half
      const yEnd = baseY + half
      this.y = yStart
      let particleFired = false
      let shimmerFired = false
      const proxy = { p: 0 }
      const duration = selectDuration('dur-quick', false) // 200ms
      const t = new Tween(proxy, this._tweenGroup)
        .to({ p: 1 }, duration)
        .easing(easing)
        .onUpdate(() => {
          const p = proxy.p // bouncy: can exceed 1.0 mid-curve (overshoot)
          // Scale lerps 0.45 → 1.0 in p; the easing's >1 excursion overshoots
          // past 1.0 (peaks ~1.08 around the bounce) then settles to exactly 1.
          const s = SCALE_FROM + (1 - SCALE_FROM) * p
          this.scale.set(s)
          this.alpha = Math.min(1, Math.max(0, p))
          this.y = yStart + (yEnd - yStart) * p
          // Emit the rune-carve particle at midpoint (≥50% progress). Emit at
          // the cell CENTER in parent space (= top-left + half) — top-left is
          // (this.x - half) because of the pivot compensation.
          if (!particleFired && this.alpha >= 0.5) {
            particleFired = true
            this._particles.emit({
              kind: 'rune-carve',
              count: 6,
              reduced: false,
              // Pivot compensation put the visible top-left at (this.x - half,
              // this.y - half); the cell center is that + half = (this.x, this.y).
              x: this.x,
              y: this.y,
            })
          }
          // Brief shimmer accent on settle (≥90% progress) — a second light
          // burst that twinkles as the cube locks into the field.
          if (!shimmerFired && p >= 0.9) {
            shimmerFired = true
            this._particles.emit({
              kind: 'rune-carve',
              count: 4,
              reduced: false,
              x: this.x,
              y: this.y,
            })
          }
        })
        .onComplete(() => {
          // Restore: settle scale to exactly 1.0, drop the pivot, and shift
          // x/y back so the final on-board top-left is byte-identical.
          this.scale.set(1)
          this.pivot.set(0)
          this.x = baseX
          this.y = baseY
          this.alpha = 1
          this._clearActiveStateTween(t as unknown as Tween<object>)
          this.setState(cell.is_anchor ? 'idle-anchor' : 'idle')
        })
        .start()
      this._activeStateTween = t as unknown as Tween<object>
    }
  }

  private _runHoverInTween(): void {
    if (this._reducedMotion) {
      // Cross-fade only — UI-SPEC §5 hover fallback. We bump tier alpha
      // toward 1.0 (already at 1.0; this is essentially a no-op visual
      // signal — the *real* hover affordance under reduced motion is the
      // path glow drawn by WordTraceLayer in P31). Skip scale entirely.
      // For now we keep scale at 1.0 — alpha bump is the no-op marker.
      return
    }
    // 100ms ease-snap-out scale 1.0 → 1.05.
    const t = new Tween(this.scale, this._tweenGroup)
      .to({ x: 1.05, y: 1.05 }, 100)
      .easing(Easing.Quadratic.Out)
      .onComplete(() => {
        this._clearActiveStateTween(t as unknown as Tween<object>)
      })
      .start()
    this._activeStateTween = t as unknown as Tween<object>
  }

  private _runHoverOutTween(): void {
    if (this._reducedMotion) {
      this.scale.set(1.0, 1.0)
      return
    }
    const t = new Tween(this.scale, this._tweenGroup)
      .to({ x: 1.0, y: 1.0 }, 100)
      .easing(Easing.Quadratic.Out)
      .onComplete(() => {
        this._clearActiveStateTween(t as unknown as Tween<object>)
      })
      .start()
    this._activeStateTween = t as unknown as Tween<object>
  }

  private _runInTraceEnterTween(): void {
    if (!this._tierEmbellishment) return
    const t = new Tween(this._tierEmbellishment, this._tweenGroup)
      .to({ alpha: 0.7 }, 100)
      .easing(Easing.Quadratic.Out)
      .onComplete(() => {
        this._clearActiveStateTween(t as unknown as Tween<object>)
      })
      .start()
    this._activeStateTween = t as unknown as Tween<object>
  }

  private _runInTraceReleaseTween(): void {
    if (!this._tierEmbellishment) return
    const t = new Tween(this._tierEmbellishment, this._tweenGroup)
      .to({ alpha: 1.0 }, 100)
      .easing(Easing.Quadratic.Out)
      .onComplete(() => {
        this._clearActiveStateTween(t as unknown as Tween<object>)
      })
      .start()
    this._activeStateTween = t as unknown as Tween<object>
  }

  private _runHollowTween(): void {
    // Phase 32 Plan 32-05 (RUNE-35) — duration sourced from
    // selectDuration('dur-anchor-consume', reducedMotion). Standard motion =
    // 400ms (UI-SPEC §4 + tween-config.ts); reduced motion collapses to
    // 200ms cross-fade. Replaces the prior hard-coded ternary.
    const duration = selectDuration('dur-anchor-consume', this._reducedMotion)
    const easingFn = this._reducedMotion
      ? Easing.Quadratic.Out
      : Easing.Cubic.InOut
    // Snapshot the layers that need to alpha-down. Include _tintWash so the
    // owner-color band (full-saturation on Anchor cells) fades out in lockstep
    // with the letter glyph and cube texture — otherwise the colored rectangle
    // stays opaque for the entire 400 ms cross-fade and snaps invisible only
    // at onComplete → _setHollowVisibility(true), producing a jarring artifact.
    const downSets: Array<{ target: { alpha: number }; from: number }> = [
      { target: this._tileBase, from: this._tileBase.alpha },
      { target: this._letterGlyph, from: this._letterGlyph.alpha },
      { target: this._tintWash, from: this._tintWash.alpha },
    ]
    if (this._tierEmbellishment) {
      downSets.push({
        target: this._tierEmbellishment,
        from: this._tierEmbellishment.alpha,
      })
    }
    if (this._anchorSigil) {
      downSets.push({
        target: this._anchorSigil,
        from: this._anchorSigil.alpha,
      })
    }
    // Prepare hollowReplacement for cross-fade in.
    this._hollowReplacement.visible = true
    this._hollowReplacement.alpha = 0
    this._hollowReplacement.tint = this._computeHollowTint(
      this._prevCell,
      this._prevTheme,
    )

    // Drive a single "progress" object 0→1 and apply alpha to all targets
    // simultaneously (tweedle can only tween properties of one target per
    // Tween instance, so we use a proxy).
    const proxy = { p: 0 }
    const t = new Tween(proxy, this._tweenGroup)
      .to({ p: 1 }, duration)
      .easing(easingFn)
      .onUpdate(() => {
        for (const d of downSets) {
          d.target.alpha = d.from * (1 - proxy.p)
        }
        this._hollowReplacement.alpha = proxy.p
      })
      .onComplete(() => {
        this._clearActiveStateTween(t as unknown as Tween<object>)
        this._setHollowVisibility(true)
        // Stop any lingering idle pulses (Anchor pulse no longer applies).
        for (const tw of this._idleTweens) {
          this._tweenGroup.remove(tw)
        }
        this._idleTweens.length = 0
      })
      .start()

    // Phase 32 Plan 32-05 (RUNE-35) — stone-fall emit on Anchor → Hollow
    // transition. UI-SPEC §2 row 10 — replaces the Phase 30 dust-puff
    // placeholder. 8 particles (warm-grey crumble + Inkwell shards mix);
    // particles.ts gates by `reduced` so reduced motion returns []. No
    // need for a wrapping if-check here — the emitter is the single gate.
    this._particles.emit({
      kind: 'stone-fall',
      count: 8,
      reduced: this._reducedMotion,
      x: this.x + CELL_SIZE_PX / 2,
      y: this.y + CELL_SIZE_PX / 2,
    })

    this._activeStateTween = t as unknown as Tween<object>
  }

  /** Compute the addChild insert index for an anchor sigil at z=6 — counts
   * how many earlier-z children currently live on the container. */
  private _getAnchorSigilInsertIndex(): number {
    // Order below the sigil: tileBase, tier?, tintWash, crossHatch?,
    // affinityHalo, letterGlyph (4 always-present + 2 optionals).
    let idx = 4 // tileBase + tintWash + affinityHalo + letterGlyph (min)
    if (this._tierEmbellishment) idx += 1
    if (this._crossHatch) idx += 1
    return idx
  }

  /** Compute the addChild insert index for crossHatch at z=3 — it sits just
   * above the tintWash and below the affinityHalo + letterGlyph. */
  private _getCrossHatchInsertIndex(): number {
    let idx = 2 // tileBase + tintWash
    if (this._tierEmbellishment) idx += 1
    return idx
  }
}
